/**
 * React Query mutation hooks for Friends actions
 * All friend-related mutations (DELETE, POST, PUT) should use these hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants'

/**
 * Mutation hook for removing a friend or cancelling an invitation
 */
export const useRemoveFriend = () => {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  return useMutation({
    mutationFn: async (friendId: string) => {
      try {
        const endpoint = `/users/friends/${encodeURIComponent(friendId)}`
        const { data } = await apiClient<{ success: boolean; error?: string; message?: string }>(endpoint, {
          method: 'DELETE',
        })

        if (!data.success) {
          throw new Error(data.error || data.message || 'Failed to remove friend')
        }

        return { friendId, data }
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Failed to remove friend'))
      }
    },
    onSuccess: (_data, friendId) => {
      // Invalidate friends queries to refetch
      queryClient.invalidateQueries({ queryKey: ['friends'] })
      // Invalidate challenges queries since deleting a friend may have deleted 1v1 challenges
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] })
      // Invalidate friend stats cache for the removed friend so it is not reused
      queryClient.invalidateQueries({ queryKey: ['friendStats', friendId] })
    },
  })
}

/**
 * Mutation hook for updating friend match predictions
 */
export const useUpdatePredictions = () => {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  return useMutation({
    mutationFn: async (predictions: Array<{
      challengeId: string
      matchId: string
      userId: string
      prediction: 'home' | 'draw' | 'away'
      outcome: 'home' | 'draw' | 'away'
      homeScore: number
      awayScore: number
    }>) => {
      try {
        const { data } = await apiClient<{ success?: boolean; data?: unknown }>(API_ENDPOINTS.predictions.batch, {
          method: 'PUT',
          data: { predictions }, // No JSON.stringify() needed - axios handles it
        })

        return data?.success ? data?.data : null
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Failed to update predictions'))
      }
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['predictions'] })
      queryClient.invalidateQueries({ queryKey: ['friends'] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
    },
  })
}

/**
 * Mutation hook for accepting an invitation
 */
export const useAcceptInvitation = () => {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  return useMutation({
    mutationFn: async (token: string) => {
      try {
        const { data } = await apiClient<{ success?: boolean; data?: unknown }>(`${API_ENDPOINTS.invitations.accept}/${token}/accept`, {
          method: 'POST',
        })

        return data?.success ? data?.data : null
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Failed to accept invitation'))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      queryClient.invalidateQueries({ queryKey: ['invitationCounts'] })
    },
  })
}

/**
 * Mutation hook for declining an invitation
 */
export const useDeclineInvitation = () => {
  const queryClient = useQueryClient()
  const apiClient = useApiClient()

  return useMutation({
    mutationFn: async (invitationId: string) => {
      try {
        await apiClient(`${API_ENDPOINTS.invitations.accept}/${invitationId}`, {
          method: 'DELETE',
        })

        return invitationId
      } catch (error) {
        throw new Error(extractErrorMessage(error, 'Failed to decline invitation'))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['friends'] })
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      queryClient.invalidateQueries({ queryKey: ['invitationCounts'] })
    },
  })
}
